class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.overlay = this.querySelector('.js-cart-drawer-overlay');
    this.inner = this.querySelector('.cart-drawer__inner');
    this.closeButtons = this.querySelectorAll('.js-cart-drawer-close');

    this.bindEvents();
  }

  bindEvents() {
    this.closeButtons.forEach(btn => btn.addEventListener('click', this.close.bind(this)));
    this.overlay.addEventListener('click', this.close.bind(this));

    // Runs on first render and after every AJAX refresh, so the new thumbnails fade in too
    if (window.gzFadeInMedia) window.gzFadeInMedia(this);
  }

  open() {
    this.classList.remove('invisible', 'pointer-events-none');

    requestAnimationFrame(() => {
      this.overlay.classList.remove('opacity-0', 'pointer-events-none');
      this.overlay.classList.add('opacity-100', 'pointer-events-auto');

      this.inner.classList.remove('translate-y-full', 'md:translate-x-full');
      this.inner.classList.add('translate-y-0', 'md:translate-x-0');
    });

    document.body.classList.add('overflow-hidden');

    if (!this.focusTrap && window.FocusTrap) {
      this.focusTrap = new FocusTrap(this, {
        onEscape: this.close.bind(this)
      });
    }
    if (this.focusTrap) this.focusTrap.activate();

    this.pushViewCartAnalytics();
  }

  pushViewCartAnalytics() {
    const rows = this.querySelectorAll('[data-item-id]');
    if (!rows.length) return;

    window.GZAnalyticsQueue = window.GZAnalyticsQueue || [];
    window.GZAnalyticsQueue.push(function (GZAnalytics) {
      const items = Array.prototype.map.call(rows, (row) =>
        GZAnalytics.buildItemObject(GZAnalytics.readItemDataset(row))
      );
      const currency = items[0] && items[0].currency;
      const value = items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);

      GZAnalytics.pushEcommerce('view_cart', { currency: currency, value: value, items: items });
    });
  }

  close() {
    this.overlay.classList.remove('opacity-100', 'pointer-events-auto');
    this.overlay.classList.add('opacity-0', 'pointer-events-none');

    this.inner.classList.remove('translate-y-0', 'md:translate-x-0');
    this.inner.classList.add('translate-y-full', 'md:translate-x-full');

    setTimeout(() => {
      this.classList.add('invisible', 'pointer-events-none');
      document.body.classList.remove('overflow-hidden');
      if (this.focusTrap) this.focusTrap.deactivate();
    }, 300);
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

window.cartDrawerState = { isUpdating: false };

window.updateCartDrawerQuantity = async function (line, qty) {
  if (qty < 0 || window.cartDrawerState.isUpdating) return;
  window.cartDrawerState.isUpdating = true;

  // Hide all error messages first
  document.querySelectorAll('.js-qty-error').forEach(el => el.classList.add('hidden'));

  const drawerInner = document.querySelector('.cart-drawer__inner');
  if (drawerInner) drawerInner.style.opacity = '0.5';
  document.body.classList.add('cursor-wait');

  try {
    const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ line: line, quantity: qty, sections: 'cart-drawer,cart' })
    });

    const data = await response.json();

    if (!response.ok) {
      // Show error next to qty
      const errorMsg = data.description || data.message || 'Error updating cart';
      const errorEls = document.querySelectorAll('.js-qty-error-' + line);
      errorEls.forEach(el => {
        el.textContent = errorMsg;
        el.classList.remove('hidden');
      });
      return;
    }

    if (window.updateCartCount) window.updateCartCount(data.item_count);

    // remove_from_cart (spec 3.9) on "-" and Remove, add_to_cart (spec 3.5) on "+", for the units
    // changed: capture the row's data before the HTML swap removes it.
    // Queued rather than gated on window.GZAnalytics so a fast update racing analytics.js's
    // defer load still fires (readItemDataset/buildItemObject only exist once it has).
    window.GZAnalyticsQueue = window.GZAnalyticsQueue || [];
    window.GZAnalyticsQueue.push(function (GZAnalytics) {
      const row = document.getElementById('CartDrawer-Item-' + line);
      if (!row) return;
      const dataset = GZAnalytics.readItemDataset(row);
      const previousQty = parseInt(dataset.quantity, 10) || 0;
      if (qty < previousQty) {
        const removedItemData = GZAnalytics.buildItemObject(dataset, { quantity: previousQty - qty });
        GZAnalytics.pushEcommerce('remove_from_cart', { items: [removedItemData] });
      } else if (qty > previousQty) {
        const addedItemData = GZAnalytics.buildItemObject(dataset, { quantity: qty - previousQty });
        GZAnalytics.pushEcommerce('add_to_cart', { items: [addedItemData] });
      }
    });

    const currentDrawer = document.querySelector('cart-drawer');
    const parser = new DOMParser();

    if (data.sections && data.sections['cart-drawer']) {
      const newDoc = parser.parseFromString(data.sections['cart-drawer'], 'text/html');
      const newDrawer = newDoc.querySelector('cart-drawer');

      if (newDrawer && currentDrawer) {
        currentDrawer.innerHTML = newDrawer.innerHTML;
        currentDrawer.overlay = currentDrawer.querySelector('.js-cart-drawer-overlay');
        currentDrawer.inner = currentDrawer.querySelector('.cart-drawer__inner');
        currentDrawer.closeButtons = currentDrawer.querySelectorAll('.js-cart-drawer-close');
        currentDrawer.bindEvents();

        // Ensure the drawer remains open visually
        currentDrawer.classList.remove('invisible', 'pointer-events-none');
        if (currentDrawer.inner) {
          currentDrawer.inner.classList.remove('translate-y-full', 'md:translate-x-full');
          currentDrawer.inner.classList.add('translate-y-0', 'md:translate-x-0');
        }
        if (currentDrawer.overlay) {
          currentDrawer.overlay.classList.remove('opacity-0', 'pointer-events-none');
          currentDrawer.overlay.classList.add('opacity-100', 'pointer-events-auto');
        }
      } else if (!newDrawer && currentDrawer) {
        // Cart is empty
        if (typeof currentDrawer.close === 'function') {
          currentDrawer.close();
        }
        setTimeout(() => {
          currentDrawer.innerHTML = '';
          if (window.location.pathname.endsWith('/cart')) {
            window.location.reload();
          }
        }, 300);
      }
    }

    // Sync main cart if visible
    const cartSectionEl = document.getElementById('shopify-section-cart');
    if (cartSectionEl && data.sections && data.sections['cart']) {
      const cartDoc = parser.parseFromString(data.sections['cart'], 'text/html');
      const newCartSection = cartDoc.getElementById('shopify-section-cart') || cartDoc.querySelector('.shopify-section');
      if (newCartSection) {
        cartSectionEl.innerHTML = newCartSection.innerHTML;
      } else {
        cartSectionEl.innerHTML = data.sections['cart'];
      }
      if (typeof window.initCart === 'function') {
        window.initCart();
      }
    }

  } catch (error) {
    console.error('Error updating cart drawer quantity:', error);
  } finally {
    const newInner = document.querySelector('.cart-drawer__inner');
    if (newInner) newInner.style.opacity = '1';
    document.body.classList.remove('cursor-wait');
    window.cartDrawerState.isUpdating = false;
  }
}

// Intercept all add to cart forms
document.addEventListener('submit', (e) => {
  if (e.target.matches('form[action$="/cart/add"]')) {
    e.preventDefault();

    const form = e.target;
    const submitBtn = form.querySelector('[type="submit"]') || document.querySelector('[form="' + form.id + '"]');

    if (submitBtn) {
      submitBtn.disabled = true;
      const originalText = submitBtn.innerHTML;

      // Attempt to find localized text inside the button or fallback to generic
      // Since it's a generic listener, we could rely on a data attribute or global variable
      // For now, we do a simple opacity effect.
      submitBtn.innerHTML = '<span class="opacity-50">' + (window.cartStrings?.adding || 'ADDING...') + '</span>';

      const errorEls = document.querySelectorAll('.js-add-to-cart-error');
      errorEls.forEach(el => el.classList.add('hidden'));

      const formData = new FormData(form);
      // The checkout pixel reads the categories from these properties: store them in English
      const englishLevels = window.GZAnalytics && window.GZAnalytics.englishCategoryLevels(form.dataset.itemCollectionId);
      if (englishLevels) {
        for (let i = 1; i <= 5; i++) {
          const name = 'properties[_gz_item_category' + i + ']';
          if (formData.has(name)) formData.set(name, englishLevels[i - 1] || '');
        }
      }

      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })
        .then(response => response.json())
        .then(data => {
          // Not added (e.g. every unit already in the cart): say why instead of opening the unchanged cart
          if (data.status) {
            errorEls.forEach(el => {
              el.textContent = data.description || data.message || '';
              el.classList.remove('hidden');
            });
            submitBtn.disabled = false;
            submitBtn.innerHTML = originalText;
            return;
          }

          window.GZAnalyticsQueue = window.GZAnalyticsQueue || [];
          window.GZAnalyticsQueue.push(function (GZAnalytics) {
            var formDataset = GZAnalytics.readItemDataset(form);
            // buildItemObject applies overrides as-is (no type coercion), unlike source
            // fields: these two need converting from the dataset's raw strings here.
            formDataset.discount = Number(formDataset.discount) || 0;
            formDataset.index = Number(formDataset.index);
            var item = GZAnalytics.buildItemObject(
              Object.assign({}, data, { __pricesInCents: true }),
              formDataset
            );
            // Spec 3.5: add_to_cart's ecommerce object only has `items`, no top-level currency/value.
            GZAnalytics.pushEcommerce('add_to_cart', { items: [item] });
          });

          fetch(window.Shopify.routes.root + '?section_id=cart-drawer')
            .then(res => res.text())
            .then(html => {
              const newDoc = new DOMParser().parseFromString(html, 'text/html');
              const newDrawer = newDoc.querySelector('cart-drawer');
              const currentDrawer = document.querySelector('cart-drawer');

              if (newDrawer) {
                // cart/add.js does not return the cart totals, so read the count off the fresh section
                if (window.updateCartCount) window.updateCartCount(newDrawer.dataset.cartCount);

                if (currentDrawer) {
                  currentDrawer.innerHTML = newDrawer.innerHTML;
                  // Re-bind overlay and inner properties
                  currentDrawer.overlay = currentDrawer.querySelector('.js-cart-drawer-overlay');
                  currentDrawer.inner = currentDrawer.querySelector('.cart-drawer__inner');
                  currentDrawer.closeButtons = currentDrawer.querySelectorAll('.js-cart-drawer-close');
                  currentDrawer.bindEvents();
                  currentDrawer.open();
                } else {
                  document.body.appendChild(newDrawer);
                  // Wait for custom element to initialize
                  setTimeout(() => {
                    const addedDrawer = document.querySelector('cart-drawer');
                    if (addedDrawer && typeof addedDrawer.open === 'function') {
                      addedDrawer.open();
                    }
                  }, 10);
                }
              }

              if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.innerHTML = originalText;
              }
            });
        })
        .catch(error => {
          console.error('Error adding to cart:', error);
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.innerHTML = originalText;
          }
        });
    }
  }
});
